<script lang="ts">
  import app from '#app/app'
  import { isNonEmptyArray } from '#app/lib/boolean_tests'
  import Flash, { type FlashState } from '#app/lib/components/flash.svelte'
  import { icon } from '#app/lib/icons'
  import { getViewportHeight, getViewportWidth } from '#app/lib/screen'
  import Modal from '#components/modal.svelte'
  import Spinner from '#components/spinner.svelte'
  import ListingEditor from '#listings/components/listing_editor.svelte'
  import ListingsLayout from '#listings/components/listings_layout.svelte'
  import { getListingPathname, getListingsByCreators, serializeListing, type SerializedListing, type SerializedListingWithElements } from '#listings/lib/listings'
  import type { ListingWithElements } from '#server/types/listing'
  import type { UserId } from '#server/types/user'
  import { i18n } from '#user/lib/i18n'
  import { mainUser } from '#user/lib/main_user'

  const {
    usersIds,
  }: {
    usersIds: UserId[]
  } = $props()

  let listings: SerializedListingWithElements[] = $state([])
  let flash: FlashState = $state()
  const paginationSize = Math.trunc(getViewportWidth() / 100) + 5
  let offset = paginationSize
  let windowScrollY = $state(0)
  let listingBottomEl: HTMLElement = $state()
  let hasMore = $state(true)

  async function getNextListingsBatch (offset: number, limit: number) {
    try {
      const { listings: newListings } = await getListingsByCreators({
        usersIds,
        withElements: true,
        limit,
        offset,
      }) as { listings: ListingWithElements[] }
      if (isNonEmptyArray(newListings)) {
        listings = [ ...listings, ...newListings.map(serializeListing) ]
        offset += paginationSize
      }

      if (newListings.length < paginationSize) hasMore = false
    } catch (err) {
      flash = err
    }
  }

  const waitingForInitialListings = getNextListingsBatch(0, paginationSize)

  const isMainUser = $derived(usersIds[0] === mainUser?._id)

  let showListingCreationModal = $state(false)
  async function showNewListing (newListing: SerializedListing) {
    try {
      showListingCreationModal = false
      app.navigateAndLoad(getListingPathname(newListing._id))
    } catch (err) {
      flash = err
    }
  }

  let fetching = false
  async function fetchMore () {
    if (fetching || hasMore === false) return
    fetching = true

    await getNextListingsBatch(offset, paginationSize)
    offset += paginationSize
    fetching = false
  }

  $effect(() => {
    if (listingBottomEl != null && hasMore) {
      const screenBottom = windowScrollY + getViewportHeight()
      if (screenBottom + 100 > listingBottomEl.offsetTop) fetchMore()
    }
  })
</script>

<svelte:window bind:scrollY={windowScrollY} />
<div class="user-listings">
  {#await waitingForInitialListings}
    <Spinner />
  {:then}
    {#if isMainUser}
      <div class="controls">
        <button
          class="tiny-button light-blue"
          aria-haspopup="dialog"
          onclick={() => showListingCreationModal = true}
        >
          {@html icon('plus')}
          {i18n('Create a new list')}
        </button>
      </div>
    {/if}
    <ListingsLayout {listings} />
    {#if hasMore}
      <p bind:this={listingBottomEl}>
        <Spinner center={true} />
      </p>
    {/if}
  {/await}
  <Flash state={flash} />
</div>

{#if showListingCreationModal}
  <Modal onCloseModal={() => showListingCreationModal = false}>
    <ListingEditor
      layoutTitle={i18n('Create a new list')}
      onListingEditorDone={showNewListing}
    />
  </Modal>
{/if}

<style lang="scss">
  @import "general/scss/utils";
  .user-listings{
    margin: 0 auto;
    padding: 0.5em;
  }
  .controls{
    @include display-flex(row, flex-end, flex-end);
    flex: 1 0 auto;
    margin-inline-end: 0.7em;
    margin-block-end: 0.5em;
    button{
      margin: 0;
    }
  }
  /* Small screens */
  @media screen and (width < $small-screen){
    .user-listings{
      padding: 0.5em 0;
    }
  }
</style>
